import { requestWithAbort, type RequestResponse } from '@atlassian/twg-common/request';
import { getWorkspaceAri } from '@atlassian/twg-query-builder/nl-2-cypher';
import type {
	QueryNL2CypherPayload,
	NL2CypherResponse,
} from '@atlassian/twg-query-builder/nl-2-cypher/types';

type RequestNL2CypherExtraConfigs = {
	cypherOnly?: boolean;
	external?: boolean;
	additionalHeaders?: HeadersInit;
};

/**
 * Lower-level NL2Cypher request that accepts a full payload and optional extra configs.
 * See: https://developer.atlassian.com/platform/twg-api/rest/api-group-ai-chat/#api-api-v2-nl2cypher-chat-completions-post
 */
export const requestNL2Cypher = (
	payload: QueryNL2CypherPayload,
	cloudId: string,
	extraConfigs?: RequestNL2CypherExtraConfigs,
): {
	controller: AbortController;
	execute: Promise<RequestResponse<NL2CypherResponse>>;
} => {
	const cypherOnly = extraConfigs?.cypherOnly ?? false;

	const { controller, execute } = requestWithAbort<NL2CypherResponse>(
		'post',
		`/gateway/api/twg-api-v2/${cloudId}/v2/nl2cypher/${
			extraConfigs?.external ? 'external/' : ''
		}chat/completions`,
		payload,
		{
			credentials: 'include',
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Query-Context': getWorkspaceAri(cloudId),
			...(cypherOnly ? { 'X-Cypher-Only': 'true' } : {}),
			...extraConfigs?.additionalHeaders,
		},
		[200, 400], // 200 => good, 400 => bad query / couldn't generate cypher
	);
	return { controller, execute };
};
